<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container merge-page">
      <header class="page-header">
        <h1>Ticket tecnici duplicati</h1>
        <p class="page-intro">
          Elenco degli utenti che hanno più ticket nella collection <code>assistenzatecnica</code>.
          Unifica le conversazioni in un solo ticket mantenendo la sequenza dei messaggi e le date originali.
        </p>
        <div class="header-actions">
          <button
            type="button"
            class="btn btn-ghost"
            :disabled="isLoading || mergingUserId"
            @click="reload"
          >
            <span class="material-icons" aria-hidden="true">autorenew</span>
            Aggiorna elenco
          </button>
        </div>
      </header>

      <section class="card results-card">
        <div class="card-header">
          <h2>Ticket con stesso utente</h2>
          <span v-if="isLoading" class="inline-loader">Analisi in corso…</span>
        </div>

        <div v-if="errorMessage" class="alert alert-error">
          {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="alert alert-success">
          {{ successMessage }}
        </div>

        <div v-if="!isLoading && duplicateGroups.length === 0" class="empty-state">
          Nessun ticket duplicato rilevato al momento.
        </div>

        <div v-else class="groups-list">
          <article
            v-for="group in duplicateGroups"
            :key="group.userId"
            class="duplicate-card"
          >
            <header class="duplicate-card__header">
              <div>
                <h3>
                  {{ group.user?.name || '—' }}
                  {{ group.user?.surname || '' }}
                </h3>
                <p class="duplicate-card__uid">
                  UID: <span>{{ group.userId }}</span>
                </p>
                <p v-if="group.user?.email" class="duplicate-card__email">
                  {{ group.user.email }}
                </p>
                <p v-if="group.user?.studio" class="duplicate-card__studio">
                  {{ group.user.studio }}
                </p>
              </div>
              <div class="duplicate-card__meta">
                <span class="badge badge-count">
                  {{ group.tickets.length }} ticket
                </span>
                <span class="badge">
                  {{ group.totalConversations }} messaggi
                </span>
                <span class="duplicate-card__updated">
                  Ultimo aggiornamento: {{ formatDate(group.lastActivity) }}
                </span>
              </div>
            </header>

            <div class="duplicate-card__body">
              <table>
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Status</th>
                    <th>Tipo</th>
                    <th>Messaggi</th>
                    <th>Ultimo messaggio</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="ticket in group.tickets" :key="ticket.id">
                    <td class="ticket-id">
                      <span class="ticket-id__code">{{ ticket.id }}</span>
                      <span v-if="ticket.idTicket && ticket.idTicket !== ticket.id" class="ticket-id__alias">
                        ({{ ticket.idTicket }})
                      </span>
                    </td>
                    <td>
                      {{ ticket.status || '—' }}
                    </td>
                    <td>
                      {{ ticket.tipoTicket || '—' }}
                    </td>
                    <td>
                      {{ ticket.meta.conversationCount }}
                    </td>
                    <td>
                      <div class="last-message">
                        <span class="last-message__date">
                          {{ formatDate(ticket.meta.lastMessageMs) }}
                        </span>
                        <span class="last-message__preview">
                          {{ ticket.meta.lastMessagePreview }}
                        </span>
                      </div>
                    </td>
                    <td class="actions-cell">
                      <button
                        type="button"
                        class="icon-button"
                        title="Apri conversazione"
                        @click="openConversation(ticket.id)"
                      >
                        <span class="material-icons">open_in_new</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <footer class="duplicate-card__footer">
              <button
                type="button"
                class="btn btn-primary staff-button-positive"
                :disabled="mergingUserId === group.userId"
                @click="mergeTickets(group.userId)"
              >
                <span class="material-icons" aria-hidden="true">sync_alt</span>
                Unisci ticket
              </button>
            </footer>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  collection,
  doc,
  documentId,
  getDocs,
  getFirestore,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import { defineComponent, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import SidebarComponent from '../SidebarComponent.vue';
import { getTicketLastActivityMs, toDateValue } from '@/lib/technicalConversation';

const STATUS_PRIORITY = [
  'Aperto',
  'Bloccato',
  'Non Risposto',
  'In attesa',
  'Risposto',
  'Completato',
];

const parseTimestamp = (value) => toDateValue(value)?.getTime() || 0;

const mergeTicketConversations = (tickets) => {
  const entries = [];
  tickets.forEach((ticket, ticketIndex) => {
    let anchor = 0;
    (ticket.conversations || []).forEach((message, messageIndex) => {
      // Device dates may go backwards. Monotonic anchors interleave tickets
      // without changing the stored order of any individual conversation.
      anchor = Math.max(anchor, parseTimestamp(message?.timestamp));
      entries.push({ message, anchor, ticketIndex, messageIndex });
    });
  });
  entries.sort((a, b) => a.anchor - b.anchor
    || a.ticketIndex - b.ticketIndex || a.messageIndex - b.messageIndex);
  return entries.map(({ message }) => message);
};

const mergeChangedError = () => {
  const error = new Error('I ticket selezionati sono cambiati.');
  error.code = 'ticket-merge-changed';
  return error;
};

const stripHtml = (value = '') =>
  String(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const truncate = (value = '', limit = 160) => {
  if (!value) return '—';
  const plain = stripHtml(value);
  return plain.length > limit ? `${plain.slice(0, limit)}…` : plain;
};

const sanitize = (data) => {
  const out = {};
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value !== undefined) out[key] = value;
  });
  return out;
};

export default defineComponent({
  name: 'RecuperoTicketDoppi',
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const router = useRouter();

    const isLoading = ref(false);
    const duplicateGroups = ref([]);
    const errorMessage = ref('');
    const successMessage = ref('');
    const mergingUserId = ref(null);

    const formatDate = (value) => {
      const ms = typeof value === 'number' ? value : parseTimestamp(value);
      if (!ms) return '—';
      try {
        return new Intl.DateTimeFormat('it-IT', {
          dateStyle: 'short',
          timeStyle: 'short',
          timeZone: 'Europe/Rome',
        }).format(new Date(ms));
      } catch {
        return '—';
      }
    };

    const computeTicketActivity = (ticket) => {
      const conversations = Array.isArray(ticket.conversations)
        ? ticket.conversations
        : [];
      const lastMessage = conversations[conversations.length - 1];

      return {
        lastMessageMs: getTicketLastActivityMs(ticket),
        lastMessagePreview: truncate(lastMessage?.content || ''),
        conversationCount: conversations.length,
      };
    };

    const fetchUsers = async (userIds) => {
      const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
      const result = {};
      const chunkSize = 10;

      for (let i = 0; i < uniqueIds.length; i += chunkSize) {
        const chunk = uniqueIds.slice(i, i + chunkSize);
        const q = query(collection(db, 'users'), where(documentId(), 'in', chunk));
        const snap = await getDocs(q);
        snap.forEach((docSnap) => {
          result[docSnap.id] = docSnap.data() || {};
        });
      }

      return result;
    };

    const buildGroupSummary = (tickets) => {
      let lastActivity = 0;
      let totalConversations = 0;

      const decoratedTickets = tickets.map((ticket) => {
        const meta = computeTicketActivity(ticket);
        if (meta.lastMessageMs > lastActivity) {
          lastActivity = meta.lastMessageMs;
        }
        totalConversations += meta.conversationCount;
        return {
          ...ticket,
          meta,
        };
      });

      decoratedTickets.sort(
        (a, b) => b.meta.lastMessageMs - a.meta.lastMessageMs
      );

      return {
        tickets: decoratedTickets,
        lastActivity,
        totalConversations,
      };
    };

    const reload = async () => {
      errorMessage.value = '';
      successMessage.value = '';
      isLoading.value = true;
      duplicateGroups.value = [];
      try {
        const snapshot = await getDocs(collection(db, 'assistenzatecnica'));
        const grouped = new Map();

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() || {};
          const userId = data.userId;
          if (!userId) return;
          const record = {
            ...data,
            id: docSnap.id,
          };
          if (!grouped.has(userId)) {
            grouped.set(userId, []);
          }
          grouped.get(userId).push(record);
        });

        const duplicates = [];
        grouped.forEach((tickets, userId) => {
          if (!userId || tickets.length < 2) return;
          const summary = buildGroupSummary(tickets);
          duplicates.push({
            userId,
            ...summary,
          });
        });

        duplicates.sort((a, b) => b.lastActivity - a.lastActivity);

        const userData = await fetchUsers(duplicates.map((item) => item.userId));
        duplicates.forEach((item) => {
          item.user = userData[item.userId] || null;
        });

        duplicateGroups.value = duplicates;
      } catch (error) {
        console.error('Errore durante il recupero dei ticket duplicati:', error);
        errorMessage.value =
          'Impossibile recuperare i ticket duplicati. Riprovare più tardi.';
      } finally {
        isLoading.value = false;
      }
    };

    const determineStatus = (tickets) => {
      const statuses = tickets
        .map((ticket) => String(ticket.status || '').trim())
        .filter(Boolean);
      if (statuses.length === 0) return 'Risposto';
      const normalized = statuses.map((status) => status.toLowerCase());
      for (const candidate of STATUS_PRIORITY) {
        const candidateLower = candidate.toLowerCase();
        if (normalized.includes(candidateLower)) {
          return candidate;
        }
      }
      return statuses[0];
    };

    const determineTipoTicket = (tickets, fallback) => {
      const candidates = tickets
        .map((ticket) => ticket.tipoTicket)
        .filter(Boolean);
      if (candidates.length === 0) return fallback || null;
      return candidates[0];
    };

    const mergeTickets = async (userId) => {
      if (!userId || mergingUserId.value) return;
      errorMessage.value = '';
      successMessage.value = '';
      mergingUserId.value = userId;

      try {
        const q = query(
          collection(db, 'assistenzatecnica'),
          where('userId', '==', userId)
        );
        const snapshot = await getDocs(q);
        // The query selects references only. Its payload may already be stale
        // when a customer sends another message, so the transaction re-reads it.
        const selectedRefs = snapshot.docs.map((docSnap) => docSnap.ref);

        if (selectedRefs.length < 2) {
          await reload();
          successMessage.value =
            'Non ci sono più ticket duplicati per questo utente.';
          return;
        }

        const result = await runTransaction(db, async (transaction) => {
          const liveSnapshots = await Promise.all(
            selectedRefs.map((ticketRef) => transaction.get(ticketRef))
          );
          const tickets = liveSnapshots.map((docSnap) => {
            if (!docSnap.exists()) throw mergeChangedError();
            const data = docSnap.data();
            if (data.userId !== userId
              || (data.conversations != null && !Array.isArray(data.conversations))) {
              throw mergeChangedError();
            }
            return { ...data, id: docSnap.id };
          });
          const sortedTickets = tickets.sort((a, b) => {
            const aTs = parseTimestamp(a.timestamp) || parseTimestamp(a.createdAt);
            const bTs = parseTimestamp(b.timestamp) || parseTimestamp(b.createdAt);
            return aTs - bTs || a.id.localeCompare(b.id);
          });
          const targetTicket = sortedTickets[0];
          const otherTickets = sortedTickets.slice(1);
          const conversations = mergeTicketConversations(sortedTickets);
          const lastDatedMessage = [...conversations].reverse()
            .find((message) => toDateValue(message?.timestamp));
          const latestTicket = sortedTickets.reduce((latest, ticket) =>
            getTicketLastActivityMs(ticket) > getTicketLastActivityMs(latest)
              ? ticket : latest, targetTicket);
          const mergedFromTickets = Array.from(new Set(sortedTickets.flatMap((ticket) => [
            ...(Array.isArray(ticket.mergedFromTickets) ? ticket.mergedFromTickets : []),
            ...(ticket.id === targetTicket.id ? [] : [ticket.id]),
          ]))).filter((ticketId) => ticketId !== targetTicket.id);

          transaction.update(doc(db, 'assistenzatecnica', targetTicket.id), sanitize({
            conversations,
            status: determineStatus(sortedTickets),
            tipoTicket:
              latestTicket.tipoTicket ||
              determineTipoTicket(sortedTickets, targetTicket.tipoTicket),
            operatorId: latestTicket.operatorId || targetTicket.operatorId,
            title: latestTicket.title || targetTicket.title,
            timestamp: lastDatedMessage?.timestamp ?? targetTicket.timestamp,
            read: false,
            flagged: sortedTickets.some((ticket) => ticket.flagged === true),
            mergedFromTickets,
          }));
          // Only the documents read by this transaction can be removed. A new
          // ticket created after the query remains available for the next reload.
          otherTickets.forEach((ticket) => {
            transaction.delete(doc(db, 'assistenzatecnica', ticket.id));
          });
          return { targetId: targetTicket.id, sourceIds: otherTickets.map((ticket) => ticket.id) };
        });

        await reload();
        successMessage.value = `Ticket uniti correttamente (mantieni ${result.targetId}, eliminati ${result.sourceIds.join(', ')}).`;
      } catch (error) {
        console.error('Errore durante la fusione dei ticket:', error);
        if (error.code === 'ticket-merge-changed') {
          await reload();
          errorMessage.value = 'I ticket selezionati sono cambiati. Nessuna fusione eseguita; controlla l’elenco aggiornato.';
        } else {
          errorMessage.value = 'Impossibile completare la fusione dei ticket. Riprovare.';
        }
      } finally {
        mergingUserId.value = null;
      }
    };

    const openConversation = (ticketId) => {
      if (!ticketId) return;
      router.push({
        name: 'AssistenzaTecnicaConversation',
        params: { ticketId },
      });
    };

    onMounted(reload);

    return {
      duplicateGroups,
      isLoading,
      errorMessage,
      successMessage,
      mergingUserId,
      formatDate,
      reload,
      mergeTickets,
      openConversation,
    };
  },
});
</script>

<style scoped>
.merge-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 48px;
}

.page-header {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 24px 28px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.page-header h1 {
  font-size: 1.75rem;
  margin: 0;
  color: #1e293b;
}

.page-intro {
  margin: 0;
  color: #475569;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  justify-content: flex-end;
}

.card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.results-card {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #0f172a;
}

.inline-loader {
  color: #2563eb;
  font-weight: 600;
}

.alert {
  margin: 16px 24px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 0.95rem;
}

.alert-error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.alert-success {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.empty-state {
  padding: 32px 24px;
  text-align: center;
  color: #475569;
  font-size: 1.05rem;
}

.groups-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}

.duplicate-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.duplicate-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.duplicate-card__header h3 {
  margin: 0;
  font-size: 1.35rem;
  color: #1e293b;
}

.duplicate-card__uid,
.duplicate-card__email,
.duplicate-card__studio {
  margin: 4px 0;
  color: #475569;
}

.duplicate-card__uid span {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.9rem;
}

.duplicate-card__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  color: #475569;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  color: #0f172a;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.85rem;
}

.badge-count {
  background: #2563eb;
  color: #ffffff;
}

.duplicate-card__updated {
  font-size: 0.85rem;
}

.duplicate-card__body table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.duplicate-card__body th {
  text-align: left;
  padding: 10px;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
}

.duplicate-card__body td {
  padding: 10px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  color: #334155;
}

.ticket-id {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ticket-id__code {
  font-family: 'Roboto Mono', monospace;
}

.ticket-id__alias {
  font-size: 0.85rem;
  color: #64748b;
}

.last-message {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.last-message__date {
  font-size: 0.85rem;
  color: #475569;
}

.last-message__preview {
  font-size: 0.9rem;
  color: #0f172a;
}

.actions-cell {
  width: 48px;
}

.duplicate-card__footer {
  display: flex;
  justify-content: flex-end;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  padding: 10px 18px;
}

.btn-primary {
  background: #2563eb;
  color: #ffffff;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-ghost {
  background: #f8fafc;
  color: #1d4ed8;
}

.btn-ghost:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #cbd5f5;
  background: #f8fafc;
  color: #1d4ed8;
  cursor: pointer;
}

.icon-button:hover {
  background: #e0e7ff;
}

.icon-button .material-icons {
  font-size: 20px;
}

@media (max-width: 960px) {
  .duplicate-card__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .duplicate-card__meta {
    align-items: flex-start;
  }

  .duplicate-card__body table,
  .duplicate-card__body thead,
  .duplicate-card__body tbody,
  .duplicate-card__body th,
  .duplicate-card__body td,
  .duplicate-card__body tr {
    display: block;
  }

  .duplicate-card__body th {
    border-bottom: none;
  }

  .duplicate-card__body tr {
    margin-bottom: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 12px;
    background: #f8fafc;
  }

  .duplicate-card__body td {
    border-bottom: none;
  }

  .actions-cell {
    width: auto;
    margin-top: 8px;
  }
}
</style>
